import {useContext, useState} from 'react'

import Context from '@s-ui/react-context'
import {checkConstraintsFactory, formBuilderChangeFieldById, formBuilderPickFieldById} from '@s-ui/react-form-builder'
import {useMount} from '@s-ui/react-hooks'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'

const isValidForm = errors => !Object.values(errors).some(error => error.length > 0)

const useFormC2bUserData = ({onAutocompleteFields, onClickSubmit, onFieldChange, onSubmitError}) => {
  const {domain, i18n} = useContext(Context)
  const {user, isLogged: isUserLogged} = useContext(AuthenticationContext)
  const config = domain.get('config')

  const {
    fieldsToTagOnBlurValue,
    formFieldIds,
    formC2bUserDataFormConfig: {fields: formFields},
    numericFieldsToBeFormatted
  } = config.get('form')

  const {
    email: emailFieldId,
    phone: phoneFieldId,
    name: nameFieldId,
    postalCode: postalCodeFieldId,
    privacyCheck: privacyCheckFieldId
  } = formFieldIds

  const initialValues = {
    [emailFieldId]: user?.email ?? '',
    [phoneFieldId]: user?.phone ?? '',
    [nameFieldId]: user?.name ?? '',
    [postalCodeFieldId]: user?.zipCode ?? ''
  }

  const [fields, setFields] = useState({...initialValues})
  const [formConfiguration, setFormConfiguration] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [isPrivacyChecked, setIsPrivacyChecked] = useState(isUserLogged)
  const [isPrivacyError, setIsPrivacyError] = useState(false)
  const [validatedErrors, setValidatedErrors] = useState({})

  useMount(() => {
    if (Object.values(initialValues).some(value => Boolean(value))) {
      onAutocompleteFields({fields: {...initialValues, [privacyCheckFieldId]: true}})
    }
  })

  if (!formConfiguration) {
    setFormConfiguration(() => {
      for (const field of formFields) {
        const {hint, label, help, constraints} = field
        if (hint) field.hint = i18n.t(hint, 1)
        if (label) field.label = i18n.t(label, 1)
        if (help) field.help = i18n.t(help, 1)
        if (constraints) {
          constraints.forEach(constraint => {
            if (constraint.message) constraint.message = i18n.t(constraint.message, 1)
          })
        }
      }

      return {
        form: {
          id: 'mt-form-c2b-user-data',
          type: 'group',
          fields: [
            {
              id: 'mt-form-c2b-user-data-fields',
              type: 'fieldset',
              display: 'block',
              fields: formFields
            }
          ]
        }
      }
    })
  }

  const commonInputProps = {
    autoComplete: 'off',
    size: 'l'
  }

  const SUIComponentsConfig = ({id}) => {
    const props = {
      [postalCodeFieldId]: {...commonInputProps, inputMode: 'numeric'},
      [nameFieldId]: commonInputProps,
      [phoneFieldId]: {...commonInputProps, inputMode: 'numeric'},
      [emailFieldId]: {...commonInputProps, inputMode: 'email'}
    }
    return props[id]
  }

  const setErrors = errors =>
    setValidatedErrors(prevErrors => {
      const allErrors = {
        ...prevErrors,
        ...errors
      }

      return allErrors
    })

  const checkConstraints = ({fieldId} = {}) => {
    const checkParams = fieldId ? {for: fieldId} : {all: true}
    const _errors = checkConstraintsFactory(formConfiguration)(checkParams)

    setErrors(_errors)
    return _errors
  }

  const handleOnBlur = fieldId => {
    if (fieldsToTagOnBlurValue.includes(fieldId)) {
      checkConstraints({fieldId})

      const {value} = formBuilderPickFieldById(formConfiguration?.form.fields, fieldId)
      value && onFieldChange({fieldId})
    }
  }

  const handleOnChange = ({__FIELD_CHANGED__, __FORM_STATE__, ...newFields}) => {
    const formFields = formConfiguration.form.fields

    if (numericFieldsToBeFormatted.includes(__FIELD_CHANGED__)) {
      let formattedValue = newFields[__FIELD_CHANGED__].replace(/\D/g, '')

      if (__FIELD_CHANGED__ !== postalCodeFieldId) {
        formattedValue = Number(formattedValue).toString()
        formattedValue = formattedValue === '0' ? '' : formattedValue
      }

      newFields[__FIELD_CHANGED__] = formattedValue
      formBuilderChangeFieldById(formFields, __FIELD_CHANGED__, {
        value: formattedValue
      })
    } else if (!fieldsToTagOnBlurValue.includes(__FIELD_CHANGED__)) {
      checkConstraints({fieldId: __FIELD_CHANGED__})
    }

    setFields({...newFields})
  }

  const handleOnSubmit = async () => {
    setIsLoading(true)

    const errors = checkConstraints()

    if (!isPrivacyChecked) {
      setIsPrivacyError(true)
    }

    if (isValidForm(errors) && isPrivacyChecked) {
      const {name, email, postalCode, phone} = fields
      try {
        await onClickSubmit({userData: {name, email, postalCode, phone}})
      } finally {
        setIsLoading(false)
      }
    } else {
      onSubmitError({errors: {...errors, [privacyCheckFieldId]: isPrivacyChecked ? [] : [`error`]}})
      setIsLoading(false)
    }
  }

  const handleChangePrivacyCheckbox = value => {
    onFieldChange({fieldId: privacyCheckFieldId})
    setIsPrivacyChecked(value)
    setIsPrivacyError(false)
  }

  return {
    // config
    formConfiguration,
    initialValues,
    SUIComponentsConfig,
    // state
    isLoading,
    isPrivacyChecked,
    isPrivacyError,
    isUserLogged,
    validatedErrors,
    // handlers
    handleChangePrivacyCheckbox,
    handleOnBlur,
    handleOnChange,
    handleOnSubmit,
    setValidatedErrors
  }
}

export default useFormC2bUserData
